import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PublicLayout from "@/components/PublicLayout";
import { Field, StatusPill } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";

const FAQ = [
  ["Is Triply really free for unlimited trips?", "Yes. The Free plan has no trip limit. Each trip can have up to 10 travelers on Free, 30 on Premium and 100 on Pro."],
  ["Does Triply collect money from travelers?", "No. Organizers collect money the way the group already does (bank transfer, cash, payment apps) and record it in Triply. Recorded payments are labeled as manual and are not verified by Triply."],
  ["How is my total calculated?", "Your total = your share of shared expenses + individually assigned costs + optional activities you opted into + your room share + any extra charges − credits. Every line is listed on your 'My trip' page."],
  ["What happens to optional activities?", "Only travelers who opt in are charged. If you change your mind before the deadline, your total updates instantly."],
  ["Who can see my flight details and preferences?", "Only you and the trip's organizers. Other travelers only see names and RSVP status."],
  ["What happens if I downgrade?", "Nothing is deleted. Trips, travelers, payments and history all stay. You just can't use premium features (like auto room assignment or exports) or add travelers above the Free limit."],
  ["How do I invite people?", "Open a trip → Travelers. Share the invite link or add people by email. You can resend or revoke invitations anytime."],
  ["How do I delete my account or export my data?", "Go to Account settings. Export downloads a copy of your data; deletion is permanent."],
];

function TicketForm() {
  const [f, setF] = useState({ category: "question", subject: "", message: "" });
  const [list, setList] = useState([]);
  const load = () => api.get("/support/tickets").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/support/tickets", f); toast.success("Thanks! We received your message."); setF({ category: "question", subject: "", message: "" }); load(); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <div className="card p-6">
      <h2 className="text-xl font-bold">Contact support</h2>
      <p className="mt-1 text-sm text-slate-600">Billing, security and privacy requests are flagged as high priority for a human to review.</p>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <Field label="Topic" htmlFor="tcat">
          <select id="tcat" className="nselect" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="ticket-category-select">
            {["question", "bug", "billing", "security", "privacy", "feedback"].map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
          </select>
        </Field>
        <Field label="Subject" htmlFor="tsub"><Input id="tsub" required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="ticket-subject-input" /></Field>
        <Field label="Message" htmlFor="tmsg"><Textarea id="tmsg" required rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="ticket-message-input" /></Field>
        <Button type="submit" className="rounded-full" data-testid="ticket-submit-button">Send message</Button>
      </form>
      {list.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="ticket-list">
          {list.map((t) => <div key={t.ticket_id} className="flex items-center justify-between rounded-xl bg-slate-50 p-3 text-sm"><span>{t.subject} · {fmtDate(t.created_at)}</span><StatusPill status={t.priority === "high" ? "needed" : "none"} label={t.status} /></div>)}
        </div>
      )}
    </div>
  );
}

export default function Help() {
  const { user } = useAuth();
  return (
    <PublicLayout>
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 lg:grid-cols-[3fr_2fr]">
        <div>
          <h1 className="text-4xl font-extrabold">Help & support</h1>
          <Accordion type="single" collapsible className="mt-8">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={q} value={`q${i}`}>
                <AccordionTrigger className="text-left font-semibold" data-testid={`faq-item-${i}`}>{q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        <div>{user ? <TicketForm /> : <div className="card p-6"><h2 className="text-xl font-bold">Need a hand?</h2><p className="mt-2 text-sm text-slate-600">Sign in to send a message to support and track replies.</p></div>}</div>
      </div>
    </PublicLayout>
  );
}
